import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchAssetTimeline } from '../../services/assetOperationalApi'
import intelStyles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

interface Props {
  assetId: string | number
}

export default function AssetTimelineSection({ assetId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [events, setEvents] = React.useState<Awaited<ReturnType<typeof fetchAssetTimeline>>['events']>([])

  React.useEffect(() => {
    if (!token) return
    let cancelled = false
    setLoading(true)
    fetchAssetTimeline(token, assetId, orgId)
      .then((data) => {
        if (!cancelled) setEvents(data.events || [])
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load timeline')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, assetId, orgId])

  if (loading) return <p>{t('common.loading')}</p>
  if (error) return <p className="text-red-600 text-sm">{error}</p>
  if (!events.length) {
    return <p className="text-sm text-gray-500">{t('assets.assetPassport.timelineEmpty', 'No operational events recorded yet.')}</p>
  }

  return (
    <ul className={intelStyles.timelineList}>
      {events.map((ev) => (
        <li key={ev.id} className={intelStyles.timelineItem}>
          <strong>{ev.title}</strong>
          <div className={intelStyles.timelineMeta}>
            {t(`assets.assetPassport.timelineKind.${ev.kind}`, ev.kind)} ·{' '}
            {ev.at ? new Date(ev.at).toLocaleString() : '—'}
            {ev.severity ? ` · ${ev.severity}` : ''}
          </div>
        </li>
      ))}
    </ul>
  )
}
